import { useContext } from 'react';

import { Fragment } from '@atlaskit/editor-prosemirror/model';
import { safeInsert } from '@atlaskit/editor-prosemirror/utils';

import { EditorContextPanelContext } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContext.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import type { Snippet } from '../snippetTypes.ts';

/**
 * Hook for inserting a snippet at the current cursor position in the editor.
 *
 * Uses the editor API from EditorContextPanelContext to execute a transaction
 * that inserts the snippet body content at the current selection.
 *
 * @returns A function to insert a snippet, returns true on success, false on failure
 */
export const useInsertSnippet = () => {
	const { editorAPI } = useContext(EditorContextPanelContext);

	return (snippet: Snippet): boolean => {
		if (!editorAPI?.core?.actions?.execute) {
			return false;
		}

		const body = snippet.body;
		if (!body) {
			return false;
		}

		try {
			editorAPI.core.actions.execute(({ tr }) => {
				const schema = tr.doc.type.schema;

				// Convert snippet body to ProseMirror content
				// Body can be either a single ADFEntity or an array of ADFEntity
				const content = Array.isArray(body)
					? Fragment.fromJSON(schema, body)
					: schema.nodeFromJSON(body);

				// Insert content at current selection using safeInsert
				// - position: undefined = use current selection position
				// - tryToReplace: true = replace selection if something is selected
				safeInsert(content, undefined, true)(tr);

				return tr;
			});

			return true;
		} catch (error) {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.INTEGRATIONS,
			});
			return false;
		}
	};
};
